/**
 * Whether the mobile shell replaces the classic app.
 * `VITE_APP_MOBILE_SHELL` at build time or the localStorage override at runtime.
 */
import type { Theme } from '@mui/material/styles';

import { darkTokens } from 'mobile/theme/tokens';

const OVERRIDE_KEY = 'a1.mobileShell';

const readOverride = (): string | null => {
  try {
    return localStorage.getItem(OVERRIDE_KEY);
  } catch {
    // Safari private mode throws on localStorage access.
    return null;
  }
};

export const isMobileShellEnabled = (): boolean => {
  const override = readOverride();
  if (override === 'on') return true;
  if (override === 'off') return false;

  return import.meta.env.VITE_APP_MOBILE_SHELL === 'true';
};

export const statusBarBackground = (theme: Theme): string => (isMobileShellEnabled() ? darkTokens.bg : theme.palette.primary.main);

const setOverride = (value: 'on' | 'off' | null) => {
  try {
    if (value === null) localStorage.removeItem(OVERRIDE_KEY);
    else localStorage.setItem(OVERRIDE_KEY, value);
  } catch {
    // Storage unavailable.
  }
};

if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__atlasMobileShell = {
    on: () => {
      setOverride('on');
      window.location.reload();
    },
    off: () => {
      setOverride('off');
      window.location.reload();
    },
    clear: () => {
      setOverride(null);
      window.location.reload();
    },
    status: () => ({ override: readOverride(), buildFlag: import.meta.env.VITE_APP_MOBILE_SHELL ?? null, enabled: isMobileShellEnabled() })
  };
}

export default isMobileShellEnabled;
